import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import PressVerse from './pages/PressVerse';
import TVVerse from './pages/TVVerse';
import RadioVerse from './pages/RadioVerse';
import EldritchDescent from './pages/EldritchDescent';
import EldritchDescentX from './pages/EldritchDescentX';
import IronFury from './pages/IronFury';
import Rudis from './pages/Rudis';
import MatchdayEmpire from './pages/MatchdayEmpire';
import ChronoArena from './pages/ChronoArena';
import CaseUnknown from './pages/CaseUnknown';
import PromptWick from './pages/PromptWick';
import MyRankFlow from './pages/MyRankFlow';
import Crxify from './pages/Crxify';
import SmartMemorySaver from './pages/SmartMemorySaver';
import MyOnlineRadio from './pages/MyOnlineRadio';
import Catalina from './pages/Catalina';
import PrivacyCatalina from './pages/PrivacyCatalina';
import Privacy from './pages/Privacy';
import PrivacyTVVerse from './pages/PrivacyTVVerse';
import PrivacyRadioVerse from './pages/PrivacyRadioVerse';
import PrivacyClassicVerse from './pages/PrivacyClassicVerse';
import PrivacyPressVerse from './pages/PrivacyPressVerse';
import PrivacyEldritchDescent from './pages/PrivacyEldritchDescent';
import PrivacyIronFury from './pages/PrivacyIronFury';
import PrivacyChronoArena from './pages/PrivacyChronoArena';
import PrivacyPromptWick from './pages/PrivacyPromptWick';
import PrivacySmartMemorySaver from './pages/PrivacySmartMemorySaver';
import PrivacyMyOnlineRadio from './pages/PrivacyMyOnlineRadio';
import Terms from './pages/Terms';
import Disclaimer from './pages/Disclaimer';
import Support from './pages/Support';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Category links point at /products#extensions. Scrolling to the top
    // unconditionally would land the visitor at the page header instead of the
    // section they clicked, so honour the hash when there is one.
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

const TitleUpdater = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const titles = {
      '/': 'MediaVerse Labs | Games, Apps and Extensions',
      '/products': 'Products | MediaVerse Labs',
      '/products/pressverse': 'PressVerse | AI Book Creator for Amazon',
      '/products/tvverse': 'TVVerse | Premium M3U Player for Windows',
      '/products/radioverse': 'RadioVerse | Global Radio for Windows',
      '/products/rudis': 'RUDIS: Blood for Freedom | Physics Gladiator Duels for PC',
      '/products/eldritch-descent': 'Eldritch Descent | Cosmic Horror for PC',
      '/products/eldritch-descent-x': "Eldritch Descent X | Cosmic-Horror Shoot 'em Up for PC",
      '/products/chrono-arena': 'Chrono Arena: Myth Wars | Retro Boomer-Shooter',
      '/products/promptwick': 'PromptWick | Prompt Improver for Chrome',
      '/products/myrankflow': 'MyRankFlow | White-label Backlink Agency',
      '/products/crxify': 'Crxify | AI Chrome Extension Builder',
      '/products/smart-memory-saver': 'Smart Memory Saver | Tab Sleeper for Chrome',
      '/products/my-online-radio': 'My Online Radio | Internet Radio Player for Chrome',
      '/products/catalina': 'Catalina AI Web Builder | Websites for Local Businesses',
      '/privacy': 'Privacy Policy | MediaVerse Labs',
      '/privacy/pressverse': 'PressVerse Privacy Policy | MediaVerse Labs',
      '/privacy/tvverse': 'TVVerse Privacy Policy | MediaVerse Labs',
      '/privacy/radioverse': 'RadioVerse Privacy Policy | MediaVerse Labs',
      '/privacy/eldritch-descent': 'Eldritch Descent Privacy Policy | MediaVerse Labs',
      '/privacy/chrono-arena': 'Chrono Arena: Myth Wars Privacy Policy | MediaVerse Labs',
      '/privacy/promptwick': 'PromptWick Privacy Policy | MediaVerse Labs',
      '/privacy/smart-memory-saver': 'Smart Memory Saver Privacy Policy | MediaVerse Labs',
      '/privacy/my-online-radio': 'My Online Radio Privacy Policy | MediaVerse Labs',
      '/my-online-radio/privacy': 'My Online Radio Privacy Policy | MediaVerse Labs',
      '/privacy/catalina': 'Catalina AI Web Builder Privacy Policy | MediaVerse Labs',
      '/terms': 'Terms of Service | MediaVerse Labs',
      '/disclaimer': 'Disclaimer | MediaVerse Labs',
      '/support': 'Support | MediaVerse Labs',
    };
    document.title = titles[pathname] || 'MediaVerse Labs';
  }, [pathname]);
  return null;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <TitleUpdater />
      <div className="min-h-screen flex flex-col relative">
        <Navbar />
        <main className="flex-grow pt-20">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/pressverse" element={<PressVerse />} />
            <Route path="/products/tvverse" element={<TVVerse />} />
            <Route path="/products/radioverse" element={<RadioVerse />} />
            <Route path="/products/eldritch-descent" element={<EldritchDescent />} />
            <Route path="/products/eldritch-descent-x" element={<EldritchDescentX />} />
            <Route path="/products/iron-fury" element={<IronFury />} />
            <Route path="/products/rudis" element={<Rudis />} />
            <Route path="/products/matchday-empire" element={<MatchdayEmpire />} />
            <Route path="/products/chrono-arena" element={<ChronoArena />} />
            <Route path="/products/caseunknown" element={<CaseUnknown />} />
            <Route path="/products/promptwick" element={<PromptWick />} />
            <Route path="/products/myrankflow" element={<MyRankFlow />} />
            <Route path="/products/crxify" element={<Crxify />} />
            <Route path="/products/smart-memory-saver" element={<SmartMemorySaver />} />
            <Route path="/products/my-online-radio" element={<MyOnlineRadio />} />
            <Route path="/products/catalina" element={<Catalina />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy/pressverse" element={<PrivacyPressVerse />} />
            <Route path="/privacy/tvverse" element={<PrivacyTVVerse />} />
            <Route path="/privacy/radioverse" element={<PrivacyRadioVerse />} />
            <Route path="/privacy/classicverse" element={<PrivacyClassicVerse />} />
            <Route path="/privacy/eldritch-descent" element={<PrivacyEldritchDescent />} />
            <Route path="/privacy/iron-fury" element={<PrivacyIronFury />} />
            <Route path="/privacy/chrono-arena" element={<PrivacyChronoArena />} />
            <Route path="/privacy/promptwick" element={<PrivacyPromptWick />} />
            <Route path="/privacy/smart-memory-saver" element={<PrivacySmartMemorySaver />} />
            <Route path="/privacy/my-online-radio" element={<PrivacyMyOnlineRadio />} />
            {/* Chrome Web Store field for this listing points here, so the path is served directly too. */}
            <Route path="/my-online-radio/privacy" element={<PrivacyMyOnlineRadio />} />
            <Route path="/privacy/catalina" element={<PrivacyCatalina />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/support" element={<Support />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;